import { onIdTokenChanged } from 'firebase/auth'
import { auth } from '@/features/auth/firebaseConfig'

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000

let cachedToken: string | null = null
let tokenExpiresAt = 0
let inflightRequest: Promise<string> | null = null

function setCache(token: string) {
  cachedToken = token
  // Firebase JWT exp is in seconds
  const payload = JSON.parse(atob(token.split('.')[1]))
  tokenExpiresAt = payload.exp * 1000 - TOKEN_EXPIRY_BUFFER_MS
}

// Keeps the cache in sync with Firebase's automatic refresh token rotation.
// Fires on: sign-in, sign-out, and every ~55 min when Firebase silently
// exchanges the refresh token for a new ID token.
onIdTokenChanged(auth, async user => {
  if (user) {
    const token = await user.getIdToken()
    setCache(token)
  } else {
    clearAuthTokenCache()
  }
})

export async function getAuthToken(forceRefresh = false): Promise<string> {
  const now = Date.now()

  if (!forceRefresh && cachedToken && now < tokenExpiresAt) {
    return cachedToken
  }

  // Deduplicate concurrent calls, all callers wait on the same promise
  if (!inflightRequest) {
    inflightRequest = auth.currentUser!
      .getIdToken(forceRefresh)
      .then(token => {
        setCache(token)
        return token
      })
      .finally(() => {
        inflightRequest = null
      })
  }

  return inflightRequest
}

export function clearAuthTokenCache() {
  cachedToken = null
  tokenExpiresAt = 0
  inflightRequest = null
}
